/**
 * usePublicMenuVariants Hook
 * 
 * Fetches all menu item variants for a branch in a SINGLE batched query.
 * This fixes the N+1 problem where each MenuItemCard was making its own query.
 * 
 * Performance improvement:
 * - Before: 50 menu items = 50 queries (one per item)
 * - After: 50 menu items = 1 query (batched)
 * 
 * @see COMPREHENSIVE_PERFORMANCE_AUDIT_2025.md - Part 6: Public Menu Analysis
 */

import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import type { MenuItemVariant } from '@/types/index';

export interface VariantsMap {
  [menuItemId: string]: MenuItemVariant[];
}

/**
 * Fetches all variants for multiple menu items in a single query
 * and returns them grouped by menu_item_id
 */
export const usePublicMenuVariants = (
  branchId: string | undefined,
  menuItemIds: string[]
) => {
  const queryKey = ['publicMenuVariants', branchId, menuItemIds.length > 0 ? menuItemIds.join(',') : 'none'];

  const { data, isLoading, error, refetch } = useQuery<VariantsMap>({
    queryKey,
    queryFn: async () => {
      if (!menuItemIds.length) return {};

      const { data: variants, error: queryError } = await supabase
        .from('menu_item_variants')
        .select('*')
        .in('menu_item_id', menuItemIds)
        .order('price', { ascending: true });

      if (queryError) {
        console.error('Error fetching menu item variants:', queryError);
        throw queryError;
      }

      // Group variants by menu_item_id for O(1) lookup
      const variantsMap: VariantsMap = {};
      for (const variant of variants || []) {
        const key = variant.menu_item_id;
        if (!variantsMap[key]) {
          variantsMap[key] = [];
        }
        variantsMap[key].push(variant as MenuItemVariant);
      }

      return variantsMap;
    },
    enabled: !!branchId && menuItemIds.length > 0,
    staleTime: 1000 * 60 * 5, // 5 minutes - variants don't change often
    gcTime: 1000 * 60 * 10, // 10 minutes
  });

  return {
    variantsMap: data || {},
    loading: isLoading,
    error,
    refetch,
  };
};

/**
 * Helper to get variants for a specific menu item from the map
 */
export const getVariantsForItem = (
  variantsMap: VariantsMap | undefined,
  menuItemId: string
): MenuItemVariant[] => {
  return variantsMap?.[menuItemId] || [];
};
